import React, { useCallback, useEffect, useState } from 'react';
import { TextArea, TextField } from '@ofself/shell';
import { GroupFaceIcon } from '../../components/nucleus/faces';
import NucleusPage from '../../components/nucleus/NucleusPage';
import RekeyNotice from '../../components/nucleus/RekeyNotice';
import { Facts, Note } from '../../components/nucleus/Inspector';
import { useToast } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { usePlace } from '../../nucleus/place';
import api from '../../services/api';
import { enableGroupEncryptionInitial, backfillGroupMembers } from '../../utils/groupEncryption';
import { rotateGroupKey } from '../nucleus/PeopleGroupData';
import { memberOf } from '../nucleus/PeopleData';
import { leaveConfirm, leaveGroup, leaveAsks, deleteConfirm } from '../groups/groupsData';
import { useConfirm } from '../../components/nucleus/actions';
import { FormColumn, FormBand, CopyLine } from './selfForm';
import { photoUrlOf } from './selfData';
import { plural } from '../../nucleus/format';

// A GROUP'S SETTINGS — the group itself: its name and photo, its own account
// (a login for automation), its encryption, and leaving or deleting it. Every
// call names the group. Only admins change things: a member reads the group as
// facts, and is not shown what only an admin can do (but Encryption, which says
// whether the group is encrypted). Everyone can leave; only an admin deletes.
// What happens when its last admin leaves is not a setting: they choose then.

function pickImage() {
    return new Promise((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = () => resolve(input.files?.[0] || null);
        input.click();
    });
}

export default function GroupSettingsPage() {
    const { section, page, groupId, navigate } = usePlace();
    const { user, encKeys } = useAuth();
    const { refreshGroups, switchToPersonal } = useGroup();
    const toast = useToast();
    const [g, setG] = useState(null);
    const [photo, setPhoto] = useState(null);
    const [form, setForm] = useState(null);
    const [creds, setCreds] = useState(null);
    const [members, setMembers] = useState(null);

    const load = useCallback(async () => {
        // not a member, or no such group: nothing is drawn (GroupGuard takes you out)
        const group = await api.getGroup(groupId).catch(() => null);
        if (!group) return;
        setG(group);
        setForm({ name: group.name || '', description: group.description || '' });
        setPhoto(group.avatar_url || await photoUrlOf(group.avatar_file_id));
        // who else is in it, so Leave can say what happens to the group
        const list = await api.getGroupMembers(groupId).then((r) => r?.members || null).catch(() => null);
        setMembers(list && list.length ? list.map(memberOf) : null);
    }, [groupId]);
    useEffect(() => { setG(null); setCreds(null); load(); }, [load]);
    // A hook: before the early return below, so every render calls it in the same order.
    const confirm = useConfirm();

    if (!g || !form) return <NucleusPage section={section.label} title={page.label} />;

    const admin = (g.my_role || g.role) === 'admin';
    const name = g.name || 'this group';
    const lock = admin ? {} : { disabled: true, hint: `Only an admin of ${name} can change this` };
    const dirty = form.name !== (g.name || '') || form.description !== (g.description || '');
    const after = async (message) => { if (message) toast.success(message); await refreshGroups?.(); await load(); };
    // out of the group: back to your own space, on Groups
    const gone = () => { switchToPersonal?.(); refreshGroups?.(); navigate('/groups'); };
    const meId = String(user?.id || user?.user_id || '');
    const admins = (members || []).filter((m) => m.role === 'admin');
    // what leaving does, said for who you are in it
    const leaves = !admin
        ? `Leaving ends both your contracts with ${name}: what you share with it, and what it lets you do.`
        : admins.some((m) => m.id !== meId)
            ? `Leaving ends both your contracts with ${name}. Another admin stays, so nothing else changes.`
            : `Leaving ends both your contracts with ${name}. ${members ? 'You are its last admin, so you choose then' : 'If you are its last admin you choose then'}: hand it to its longest-standing member, or close it.`;
    const count = g.member_count ?? members?.length ?? 0;

    return (
        <NucleusPage section={section.label} title={page.label} notice={<RekeyNotice group={{ ...g, id: g.id || groupId }} onDone={load} />}>
            <FormColumn>
                {admin ? (
                    <FormBand
                        title="The group"
                        aside={`${plural(count, 'member')} · your role: Admin`}
                        acts={[{
                            id: 'save', label: 'Save', primary: true,
                            disabled: !dirty || !form.name.trim(),
                            run: async () => {
                                await api.updateGroup(groupId, { ...form, name: form.name.trim() });
                                await after('Saved');
                            },
                        }]}
                    >
                        <TextField id="self-group-name" label="Name" value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <TextArea id="self-group-description" label="Description" value={form.description} rows={3} placeholder="What it’s for"
                            onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </FormBand>
                ) : (
                    <FormBand
                        title="The group"
                        aside={`${plural(count, 'member')} · your role: Member`}
                        lead={`Only an admin of ${name} can change these.`}
                    >
                        <Facts items={[
                            { label: 'Name', value: g.name || '—' },
                            admins.length > 0 && { label: admins.length === 1 ? 'Admin' : 'Admins', value: admins.map((m) => m.name).join(', ') },
                        ]} />
                        {g.description && <Note>{g.description}</Note>}
                    </FormBand>
                )}

                {admin && (
                    <FormBand
                        title="Photo"
                        lead="The group’s face on the space switcher and on its public page."
                        acts={[
                            {
                                id: 'photo', label: photo ? 'Change photo' : 'Add photo',
                                run: async () => {
                                    const file = await pickImage();
                                    if (!file) return;
                                    const res = await api.uploadGroupFile(groupId, file);
                                    await api.setGroupPhoto(groupId, (res?.node || res)?.id);
                                    await after('Photo saved');
                                },
                            },
                            photo && {
                                id: 'unphoto', label: 'Remove photo', danger: true,
                                confirm: { title: `Remove ${name}’s photo?`, body: 'The group glyph stands in until you add another.', label: 'Remove' },
                                run: async () => { await api.setGroupPhoto(groupId, null); await after('Photo removed'); },
                            },
                        ].filter(Boolean)}
                    >
                        <div className="self-photo">
                            <GroupFaceIcon url={photo || undefined} size={72} />
                        </div>
                    </FormBand>
                )}

                {admin && (
                    <FormBand
                        title="The group’s own account"
                        lead="A login for automation and apps that act as the group. New credentials replace the old password."
                        acts={[{
                            id: 'creds', label: g.group_email || creds ? 'Replace credentials' : 'Make credentials',
                            confirm: g.group_email || creds ? {
                                title: 'Replace the group’s credentials?',
                                body: 'The old password stops working at once, so anything signed in with it has to sign in again.',
                                label: 'Replace',
                            } : undefined,
                            danger: !!(g.group_email || creds),
                            run: async () => {
                                setCreds(await api.request(`/groups/${groupId}/credentials`, { method: 'POST' }));
                                toast.success('New credentials made. Copy them now.');
                            },
                        }]}
                    >
                        {!creds && <Facts items={[{ label: 'Login', value: g.group_email || 'None yet' }]} />}
                        {creds && (
                            <>
                                <CopyLine label="Email" value={creds.email} />
                                <CopyLine label="Password" value={creds.password} />
                                <Note tone="warn">Shown once. Copy them now.</Note>
                            </>
                        )}
                    </FormBand>
                )}

                <FormBand
                    title="Encryption"
                    aside={g.encryption_enabled ? 'On' : 'Off'}
                    lead={g.encryption_enabled
                        ? 'The group is encrypted. Key members who joined before they had a key, or give the group a fresh key after someone leaves.'
                        : 'The group has no key yet. Turning it on keys every member who has one.'}
                    acts={g.encryption_enabled ? [
                        {
                            id: 'backfill', label: 'Key pending members', ...lock,
                            disabled: !admin || !encKeys?.privateKey,
                            hint: !admin ? lock.hint : (!encKeys?.privateKey ? 'Unlock your key first: open an encrypted note, then come back' : undefined),
                            run: async () => {
                                const { keyed, pending } = await backfillGroupMembers(groupId, encKeys.privateKey);
                                toast.success(keyed ? `Keyed ${plural(keyed, 'pending member')}.` : `Everyone with a key already has the group key.${pending ? ` ${pending} have no key yet.` : ''}`);
                                await load();
                            },
                        },
                        {
                            id: 'rekey', label: 'Re-key', ...lock,
                            confirm: { title: `Give ${name} a fresh key?`, body: 'Every member and app with a key gets the new one. Do this after removing someone, so they can’t read what comes next.', label: 'Re-key' },
                            run: async () => {
                                const r = await rotateGroupKey(groupId);
                                await after(`Re-keyed: ${plural(r.members, 'member')}, ${plural(r.apps, 'app')}`);
                            },
                        },
                    ] : [{
                        id: 'on', label: 'Turn on encryption', primary: true, ...lock,
                        run: async () => {
                            const { keyed, pending } = await enableGroupEncryptionInitial(groupId);
                            if (!keyed) throw new Error('No members have an encryption key yet. Members must sign in once first.');
                            await after(`Encryption is on for ${plural(keyed, 'member')}${pending ? `, ${pending} pending` : ''}.`);
                        },
                    }]}
                >
                    {g.encryption_enabled && !encKeys?.privateKey && admin && (
                        <Note>Keying pending members needs your own key unlocked in this browser.</Note>
                    )}
                </FormBand>

                <FormBand
                    title="Leave"
                    lead={leaves}
                    acts={[{
                        id: 'leave', label: `Leave ${name}`, danger: true,
                        confirm: leaveAsks({ role: admin ? 'admin' : 'member', members, meId }) ? leaveConfirm({ name }) : undefined,
                        run: async () => { if (await leaveGroup(groupId, meId, confirm, { name, role: admin ? 'admin' : 'member', members })) { toast.success(`You left ${name}`); gone(); } },
                    }]}
                />

                {admin && (
                    <FormBand
                        title="Delete"
                        lead={`Deleting ${name} ends it for ${count === 1 ? 'its only member' : `all ${count} members`}: its apps are disconnected, its public page comes down, its realms and invitations are removed, every member contract ends and its nodes go to the trash. This cannot be undone.`}
                        acts={[{
                            id: 'delete', label: `Delete ${name}`, danger: true,
                            confirm: deleteConfirm(name),
                            run: async () => { await api.deleteGroup(groupId); toast.success(`${name} was deleted`); gone(); },
                        }]}
                    />
                )}
            </FormColumn>
        </NucleusPage>
    );
}
